import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Alert, Chip, CircularProgress, Button, Stack, LinearProgress } from '@mui/material';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { Hotel, Star, OpenInNew } from '@mui/icons-material';
import { parseLatLngFromObject, expandCountryForGeocode } from '../../utils/geoUtils';

const MAP_CONTAINER_STYLE = { width: '100%', height: '100%' };
const INDIA_FALLBACK = { lat: 20.5937, lng: 78.9629 };

function formatRateShort(rate, currency) {
  if (rate == null || !Number.isFinite(Number(rate))) return '';
  const n = Math.round(Number(rate));
  const sym = (currency || 'INR').toUpperCase() === 'INR' ? '₹' : (currency || '').toUpperCase() === 'USD' ? '$' : '';
  return sym ? `${sym}${n.toLocaleString('en-IN')}` : String(n);
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return { r: 76, g: 225, b: 182 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function isGoogleRateSource(source) {
  const s = String(source || '').toLowerCase();
  return s.includes('google') || s.includes('travel');
}

function rateBadgeLine(m) {
  if (m.kind === 'property') return 'Your hotel · Google Travel';
  if (isGoogleRateSource(m.source)) return 'Google Hotels / Travel nightly';
  return 'Nightly rate (saved)';
}

/** Google Hotels / Maps price pill: white capsule, icon disc, bold amount, bottom pointer */
const GOOGLE_PILL_BORDER = '#dadce0';
const GOOGLE_PILL_SHADOW = 'rgba(60, 64, 67, 0.3)';
const GOOGLE_HOTEL_PINK = '#e9426d';

function drawPillPath(ctx, x, y, w, h, cornerR) {
  const r = Math.min(cornerR, h / 2, w / 2);
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
}

/** White bed symbol (Google Hotels) inside colored circle */
function drawGoogleBedIcon(ctx, cx, cy, s = 1) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(s, s);
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.fillRect(-8.5, -6.5, 2.6, 10.5);
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(-5.8, 1.2, 14.5, 5.8, 1.1);
  } else {
    ctx.rect(-5.8, 1.2, 14.5, 5.8);
  }
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(2.8, -1.4, 3.8, 2.35, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** White house (your property marker style on Google) */
function drawGoogleHouseIcon(ctx, cx, cy, s = 1) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(s, s);
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.moveTo(0, -8.5);
  ctx.lineTo(9.2, 0.5);
  ctx.lineTo(-9.2, 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(-6.5, 0.5, 13, 8.5);
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.fillRect(-2.2, 3.2, 4.4, 5.8);
  ctx.restore();
}

function buildMapPinDataUrl(m, accentHex) {
  if (typeof document === 'undefined') return null;
  const prop = m.kind === 'property';
  const price =
    m.rate != null && Number.isFinite(Number(m.rate))
      ? formatRateShort(m.rate, m.currency)
      : '—';

  const pillH = 36;
  const pointerH = 8;
  const iconDiscR = 12.5;
  const padL = 8;
  const gap = 8;
  const padR = 11;
  const fontPx = 15;
  const measure = document.createElement('canvas').getContext('2d');
  if (!measure) return null;
  measure.font = `600 ${fontPx}px Roboto, "Google Sans", system-ui, -apple-system, sans-serif`;
  let displayPrice = price;
  while (displayPrice.length > 2 && measure.measureText(displayPrice).width > 128) {
    displayPrice = `${displayPrice.slice(0, -2)}…`;
  }
  const textW = Math.ceil(measure.measureText(displayPrice).width);
  const pillW = Math.max(108, Math.ceil(padL + iconDiscR * 2 + gap + textW + padR));
  const totalW = pillW;
  const totalH = pillH + pointerH;
  const tipX = totalW / 2;
  const tipY = totalH;
  const tipHalfW = 7;

  const canvas = document.createElement('canvas');
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = totalW * dpr;
  canvas.height = totalH * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(dpr, dpr);

  ctx.save();
  ctx.shadowColor = GOOGLE_PILL_SHADOW;
  ctx.shadowBlur = 5;
  ctx.shadowOffsetY = 1.5;
  ctx.fillStyle = '#ffffff';
  drawPillPath(ctx, 0, 0, pillW, pillH, pillH / 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(tipX - tipHalfW, pillH - 0.75);
  ctx.lineTo(tipX + tipHalfW, pillH - 0.75);
  ctx.lineTo(tipX, tipY - 0.25);
  ctx.closePath();
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.restore();

  ctx.strokeStyle = GOOGLE_PILL_BORDER;
  ctx.lineWidth = 1.1;
  drawPillPath(ctx, 0.5, 0.5, pillW - 1, pillH - 1, pillH / 2 - 0.25);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(tipX - tipHalfW + 0.3, pillH - 0.4);
  ctx.lineTo(tipX, tipY - 0.85);
  ctx.lineTo(tipX + tipHalfW - 0.3, pillH - 0.4);
  ctx.stroke();

  const iconCx = padL + iconDiscR;
  const iconCy = pillH / 2;
  const rgb = hexToRgb(accentHex);
  const discFill = prop ? `rgb(${rgb.r},${rgb.g},${rgb.b})` : GOOGLE_HOTEL_PINK;
  ctx.beginPath();
  ctx.arc(iconCx, iconCy, iconDiscR, 0, Math.PI * 2);
  ctx.fillStyle = discFill;
  ctx.fill();

  if (prop) {
    drawGoogleHouseIcon(ctx, iconCx, iconCy, 0.92);
  } else {
    drawGoogleBedIcon(ctx, iconCx, iconCy, 0.88);
  }

  ctx.fillStyle = '#202124';
  ctx.font = `600 ${fontPx}px Roboto, "Google Sans", system-ui, -apple-system, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(displayPrice, padL + iconDiscR * 2 + gap, pillH / 2 + 0.5);

  return {
    url: canvas.toDataURL('image/png'),
    width: totalW,
    height: totalH,
    anchorX: tipX,
    anchorY: tipY,
  };
}

function buildRichInfoWindowHtml(m, accentHex) {
  const title = escapeHtml(m.title);
  const rateMain =
    m.rate != null && Number.isFinite(Number(m.rate)) ? formatRateShort(m.rate, m.currency) : null;
  const badge = escapeHtml(rateBadgeLine(m));
  const prop = m.kind === 'property';
  const grad = prop
    ? `linear-gradient(135deg, ${escapeHtml(accentHex)} 0%, #0a2e26 120%)`
    : 'linear-gradient(135deg, #ec407a 0%, #4a148c 110%)';
  const rateBlock = rateMain
    ? `<div style="font-size:24px;font-weight:800;letter-spacing:-0.03em;line-height:1.05">${escapeHtml(rateMain)}</div>`
    : '<div style="font-size:14px;font-weight:600;opacity:0.92;line-height:1.3">Sync daily rate to show Google Travel price here.</div>';
  return `<div style="margin:0;padding:0;font-family:system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:232px;max-width:288px;border-radius:16px;overflow:hidden;box-shadow:0 16px 48px rgba(0,0,0,0.22);border:1px solid rgba(255,255,255,0.2)">
    <div style="background:${grad};color:#fff;padding:14px 16px 18px;">
      <div style="font-size:9px;font-weight:800;opacity:0.9;text-transform:uppercase;letter-spacing:0.14em;margin-bottom:6px">${badge}</div>
      ${rateBlock}
    </div>
    <div style="background:#fafbfc;padding:12px 16px 14px;color:#141820;border-top:1px solid rgba(0,0,0,0.06)">
      <div style="font-size:15px;font-weight:700;line-height:1.35">${title}</div>
      <div style="margin-top:10px;font-size:11px;color:#5c6570;line-height:1.45">Nightly amounts follow your Google Travel / Hotels capture when the competitor has been rate-synced.</div>
    </div>
  </div>`;
}

function geocodeRequest(geocoder, request) {
  return new Promise((resolve) => {
    geocoder.geocode(request, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        resolve({ lat: loc.lat(), lng: loc.lng() });
      } else resolve(null);
    });
  });
}

function markersSignature(markers) {
  return JSON.stringify(
    markers.map((m) => ({
      k: m.key,
      lat: m.lat,
      lng: m.lng,
      rate: m.rate,
      cur: m.currency,
      src: m.source,
      kind: m.kind,
    })),
  );
}

/**
 * Google Maps: native Marker instances (reliable); geocode completes before treating "no pins" as final.
 */
const GoogleCompetitorMap = React.memo(function GoogleCompetitorMap({
  apiKey,
  propertyHotel,
  propertyRate,
  propertyCurrency = 'INR',
  competitors,
  accentColor = '#4ce1b6',
  isDark = false,
  focusCompetitorId = null,
  onFocusApplied,
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'nexa-competitor-map-script',
    googleMapsApiKey: apiKey,
    version: 'weekly',
  });

  const [map, setMap] = useState(null);
  const [viewMode, setViewMode] = useState('property');
  const [activeInfoKey, setActiveInfoKey] = useState(null);
  const [geocoded, setGeocoded] = useState({ property: null, competitors: {} });
  const [geocodeDone, setGeocodeDone] = useState(false);
  const geocodeRunRef = useRef(0);

  const propertyHotelRef = useRef(propertyHotel);
  const competitorsRef = useRef(competitors);
  propertyHotelRef.current = propertyHotel;
  competitorsRef.current = competitors;

  const onLoad = useCallback((m) => setMap(m), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const propertySignature = useMemo(
    () =>
      propertyHotel
        ? JSON.stringify({
            id: propertyHotel.id,
            la: propertyHotel.latitude,
            lo: propertyHotel.longitude,
            addr: propertyHotel.address,
            city: propertyHotel.city,
            pc: propertyHotel.pincode,
            name: propertyHotel.name,
            country: propertyHotel.country,
          })
        : '',
    [propertyHotel],
  );

  const competitorsSignature = useMemo(
    () =>
      JSON.stringify(
        (competitors || []).map((c) => ({
          id: c.id,
          la: c.latitude,
          lo: c.longitude,
          pid: c.place_id,
          addr: c.address,
          name: c.name,
        })),
      ),
    [competitors],
  );

  useEffect(() => {
    if (!isLoaded || !window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();
    const runId = ++geocodeRunRef.current;
    let cancelled = false;
    setGeocodeDone(false);

    (async () => {
      const ph = propertyHotelRef.current;
      const comps = competitorsRef.current;
      const next = { property: null, competitors: {} };

      const propDirect = parseLatLngFromObject(ph);
      if (propDirect) {
        next.property = propDirect;
      } else if (ph) {
        const countryLabel = expandCountryForGeocode(ph.country);
        const parts = [ph.name, ph.address, ph.city, ph.pincode, countryLabel].filter(
          (x) => x && String(x).trim(),
        );
        if (parts.length) {
          next.property = await geocodeRequest(geocoder, { address: parts.join(', ') });
        }
      }
      if (cancelled || runId !== geocodeRunRef.current) return;

      const list = Array.isArray(comps) ? comps : [];
      for (let i = 0; i < list.length; i += 1) {
        const c = list[i];
        const id = c?.id;
        if (id == null) continue;

        let pos = parseLatLngFromObject(c);
        if (pos) {
          next.competitors[id] = pos;
          continue;
        }

        const pid = c.place_id != null ? String(c.place_id).trim() : '';
        if (pid) {
          pos = await geocodeRequest(geocoder, { placeId: pid });
        }
        if (!pos && c.address && String(c.address).trim()) {
          pos = await geocodeRequest(geocoder, { address: String(c.address).trim() });
        }
        if (!pos && c.name) {
          const tail = [c.address, ph?.city, ph?.country].filter(Boolean).join(', ');
          const q = tail ? `${String(c.name).trim()}, ${tail}` : String(c.name).trim();
          if (q) pos = await geocodeRequest(geocoder, { address: q });
        }
        if (pos) next.competitors[id] = pos;
        if (cancelled || runId !== geocodeRunRef.current) return;
      }

      if (!cancelled && runId === geocodeRunRef.current) {
        setGeocoded(next);
        setGeocodeDone(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, propertySignature, competitorsSignature]);

  const effectivePropertyPos = useMemo(
    () => parseLatLngFromObject(propertyHotel) ?? geocoded.property,
    [propertyHotel, geocoded.property],
  );

  const markers = useMemo(() => {
    const list = [];
    if (effectivePropertyPos) {
      const name = (propertyHotel?.name && String(propertyHotel.name).trim()) || 'Your hotel';
      const rateLabel = formatRateShort(propertyRate, propertyCurrency) || 'You';
      list.push({
        key: 'property',
        kind: 'property',
        lat: effectivePropertyPos.lat,
        lng: effectivePropertyPos.lng,
        title: name,
        rateLabel,
        rate: propertyRate != null ? Number(propertyRate) : null,
        currency: propertyCurrency,
        source: 'google_travel',
      });
    }
    const compList = Array.isArray(competitors) ? competitors : [];
    for (let i = 0; i < compList.length; i += 1) {
      const c = compList[i];
      const id = c.id != null ? c.id : `idx-${i}`;
      const pos = parseLatLngFromObject(c) ?? geocoded.competitors[id];
      if (!pos) continue;
      const rateLabel = formatRateShort(c.average_rate, c.currency) || 'Hotel';
      list.push({
        key: `c-${id}`,
        kind: 'competitor',
        competitorId: id,
        lat: pos.lat,
        lng: pos.lng,
        title: (c.name && String(c.name).trim()) || 'Competitor',
        rateLabel,
        rate:
          c.average_rate != null && c.average_rate !== '' && Number.isFinite(Number(c.average_rate))
            ? Number(c.average_rate)
            : null,
        currency: (c.currency && String(c.currency).toUpperCase().slice(0, 3)) || 'INR',
        source: c.source || '',
      });
    }
    return list;
  }, [effectivePropertyPos, propertyHotel, propertyRate, propertyCurrency, competitors, geocoded.competitors]);

  const sig = useMemo(() => markersSignature(markers), [markers]);

  const compRowsForChips = useMemo(() => {
    const list = Array.isArray(competitors) ? competitors : [];
    const out = [];
    for (let i = 0; i < list.length; i += 1) {
      const c = list[i];
      const id = c.id != null ? c.id : `idx-${i}`;
      const pos = parseLatLngFromObject(c) ?? geocoded.competitors[id];
      if (!pos) continue;
      const rawRate = c.average_rate;
      const rate =
        rawRate != null && rawRate !== '' && Number.isFinite(Number(rawRate)) ? Number(rawRate) : null;
      out.push({
        id,
        name: (c.name && String(c.name).trim()) || 'Competitor',
        lat: pos.lat,
        lng: pos.lng,
        rate,
        currency: (c.currency && String(c.currency).toUpperCase().slice(0, 3)) || 'INR',
        source: c.source || '',
      });
    }
    return out;
  }, [competitors, geocoded.competitors]);

  const validPoints = useMemo(
    () => markers.map((m) => ({ lat: m.lat, lng: m.lng })),
    [markers],
  );

  const defaultCenter = useMemo(() => {
    if (effectivePropertyPos) return effectivePropertyPos;
    if (validPoints.length) return validPoints[0];
    return INDIA_FALLBACK;
  }, [effectivePropertyPos, validPoints]);

  const mapOptions = useMemo(
    () => ({
      fullscreenControl: true,
      streetViewControl: true,
      mapTypeControl: false,
      gestureHandling: 'greedy',
      mapTypeId: 'roadmap',
    }),
    [],
  );

  const handleMarkerDataClick = useCallback((m) => {
    setActiveInfoKey(m.key);
    if (m.kind === 'competitor') {
      setViewMode(m.key);
    } else {
      setViewMode('property');
    }
  }, []);

  const nativeMarkersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const markersByKeyRef = useRef({});
  const openInfoForMarkerRef = useRef(() => {});

  useEffect(() => {
    if (!map || !window.google?.maps || !isLoaded) return undefined;
    const g = window.google.maps;

    nativeMarkersRef.current.forEach((mk) => mk.setMap(null));
    nativeMarkersRef.current = [];
    markersByKeyRef.current = {};

    if (!infoWindowRef.current) {
      infoWindowRef.current = new g.InfoWindow();
    }
    const iw = infoWindowRef.current;

    const openInfoFor = (m) => {
      const mk = markersByKeyRef.current[m.key];
      if (!mk) return;
      iw.setContent(buildRichInfoWindowHtml(m, accentColor));
      iw.open({ map, anchor: mk });
    };
    openInfoForMarkerRef.current = openInfoFor;

    markers.forEach((m) => {
      const titleParts = [m.title, m.rateLabel].filter(Boolean);
      const pin = buildMapPinDataUrl(m, accentColor);
      const iconOpts = pin
        ? {
            url: pin.url,
            scaledSize: new g.Size(pin.width, pin.height),
            anchor: new g.Point(pin.anchorX, pin.anchorY),
          }
        : {
            url:
              m.kind === 'property'
                ? 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
                : 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
            scaledSize: new g.Size(32, 32),
            anchor: new g.Point(16, 32),
          };
      const marker = new g.Marker({
        map,
        position: { lat: m.lat, lng: m.lng },
        title: titleParts.join(' · '),
        optimized: false,
        icon: iconOpts,
      });
      markersByKeyRef.current[m.key] = marker;
      marker.addListener('click', () => {
        handleMarkerDataClick(m);
        openInfoFor(m);
      });
      nativeMarkersRef.current.push(marker);
    });

    return () => {
      nativeMarkersRef.current.forEach((mk) => mk.setMap(null));
      nativeMarkersRef.current = [];
      markersByKeyRef.current = {};
      iw.close();
    };
  }, [map, isLoaded, sig, handleMarkerDataClick, accentColor]);

  useEffect(() => {
    if (!map || !window.google?.maps || !markers.length) return;
    const g = window.google.maps;

    if (viewMode === 'all') {
      const bounds = new g.LatLngBounds();
      validPoints.forEach((p) => bounds.extend(new g.LatLng(p.lat, p.lng)));
      map.fitBounds(bounds, 72);
      const t = window.setTimeout(() => {
        const z = map.getZoom();
        if (z != null && z > 15) map.setZoom(15);
      }, 300);
      return () => window.clearTimeout(t);
    }

    if (viewMode.startsWith('c-')) {
      const m = markers.find((x) => x.key === viewMode);
      if (m) {
        map.panTo({ lat: m.lat, lng: m.lng });
        map.setZoom(16);
      }
      return undefined;
    }

    if (effectivePropertyPos) {
      map.panTo(effectivePropertyPos);
      map.setZoom(13);
    } else if (validPoints.length === 1) {
      map.panTo(validPoints[0]);
      map.setZoom(14);
    } else if (validPoints.length > 1) {
      const bounds = new g.LatLngBounds();
      validPoints.forEach((p) => bounds.extend(new g.LatLng(p.lat, p.lng)));
      map.fitBounds(bounds, 64);
    }
    return undefined;
  }, [map, viewMode, markers, validPoints, effectivePropertyPos]);

  const focusCompetitorKey =
    focusCompetitorId != null && String(focusCompetitorId).length
      ? `c-${focusCompetitorId}`
      : null;

  const appliedFocusRef = useRef(null);
  useEffect(() => {
    if (!focusCompetitorKey) {
      appliedFocusRef.current = null;
      return;
    }
    const exists = markers.some((m) => m.key === focusCompetitorKey);
    if (!exists) return;
    if (appliedFocusRef.current === focusCompetitorKey) return;
    appliedFocusRef.current = focusCompetitorKey;
    setViewMode(focusCompetitorKey);
    setActiveInfoKey(focusCompetitorKey);
    onFocusApplied?.();
  }, [focusCompetitorKey, markers, onFocusApplied]);

  const focusCompetitorOnMap = useCallback(
    (id) => {
      const key = `c-${id}`;
      setViewMode(key);
      setActiveInfoKey(key);
      const m = markers.find((x) => x.key === key);
      if (m) {
        window.requestAnimationFrame(() => openInfoForMarkerRef.current(m));
      }
    },
    [markers],
  );

  if (loadError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Google Maps failed to load. Check the API key, billing, and Maps JavaScript API enablement.
      </Alert>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: 420,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant={viewMode === 'property' ? 'contained' : 'outlined'}
          onClick={() => {
            setViewMode('property');
            setActiveInfoKey(null);
            infoWindowRef.current?.close();
          }}
          startIcon={<Star sx={{ fontSize: 18 }} />}
          sx={viewMode === 'property' ? { bgcolor: accentColor, color: '#0d1f1a', '&:hover': { bgcolor: accentColor } } : {}}
        >
          My hotel
        </Button>
        <Button
          size="small"
          variant={viewMode === 'all' ? 'contained' : 'outlined'}
          onClick={() => {
            setViewMode('all');
            setActiveInfoKey(null);
            infoWindowRef.current?.close();
          }}
        >
          All nearby
        </Button>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          mb: 2,
          p: '4px',
          borderRadius: 3,
          background: isDark
            ? `linear-gradient(145deg, ${accentColor}55 0%, rgba(120, 80, 180, 0.35) 50%, rgba(30, 32, 40, 0.9) 100%)`
            : `linear-gradient(145deg, ${accentColor} 0%, #c084fc 45%, #38bdf8 100%)`,
          boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.45)' : '0 16px 48px rgba(15, 23, 42, 0.12)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            height: 420,
            bgcolor: isDark ? 'grey.900' : 'grey.100',
          }}
        >
          {!geocodeDone && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2,
              }}
            />
          )}
          <GoogleMap
            mapContainerStyle={MAP_CONTAINER_STYLE}
            center={defaultCenter}
            zoom={effectivePropertyPos ? 13 : 12}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={mapOptions}
          >
            {null}
          </GoogleMap>
          {geocodeDone && markers.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                right: 8,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No pins: save <strong>latitude/longitude</strong> on your property (Basic details), and ensure competitors
                have coordinates from Discover / Places sync, or a <strong>place_id</strong> / address we can look up.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Jump to a price pin or open in Google Maps
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {effectivePropertyPos && propertyHotel?.name && (
          <Chip
            icon={<Star sx={{ color: accentColor, fontSize: 18 }} />}
            label={
              propertyRate != null && Number.isFinite(Number(propertyRate))
                ? `Your hotel: ${propertyHotel.name} — ${formatRateShort(Number(propertyRate), propertyCurrency)} (Google Travel)`
                : `Your hotel: ${propertyHotel.name}`
            }
            clickable
            onClick={() => {
              setViewMode('property');
              setActiveInfoKey('property');
              const m = markers.find((x) => x.key === 'property');
              if (m) window.requestAnimationFrame(() => openInfoForMarkerRef.current(m));
            }}
            onDelete={(e) => {
              e.stopPropagation();
              window.open(`https://www.google.com/maps?q=${effectivePropertyPos.lat},${effectivePropertyPos.lng}`, '_blank', 'noopener,noreferrer');
            }}
            deleteIcon={<OpenInNew sx={{ fontSize: 18 }} />}
            sx={{ borderColor: accentColor, color: accentColor }}
            variant="outlined"
          />
        )}
        {compRowsForChips.map((c) => {
          const gTag = isGoogleRateSource(c.source) && c.rate != null ? ' · Google' : '';
          const chipLabel =
            c.rate != null ? `${c.name} — ${formatRateShort(c.rate, c.currency)}${gTag}` : c.name;
          return (
          <Chip
            key={String(c.id)}
            icon={<Hotel fontSize="small" />}
            label={chipLabel}
            onClick={() => focusCompetitorOnMap(c.id)}
            onDelete={(e) => {
              e.stopPropagation();
              window.open(`https://www.google.com/maps?q=${c.lat},${c.lng}`, '_blank', 'noopener,noreferrer');
            }}
            deleteIcon={<OpenInNew sx={{ fontSize: 18 }} />}
            clickable
            variant="outlined"
          />
          );
        })}
      </Box>
    </Box>
  );
});

function CompetitorLocationMap({
  propertyHotel,
  propertyRate,
  propertyCurrency = 'INR',
  competitors,
  accentColor,
  isDark,
  focusCompetitorId = null,
  onFocusApplied,
}) {
  const apiKey =
    typeof window !== 'undefined' && window.DJANGO_CONTEXT?.googleMapsApiKey
      ? String(window.DJANGO_CONTEXT.googleMapsApiKey).trim()
      : '';

  if (!apiKey) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Set <strong>GOOGLE_MAPS_API_KEY</strong> in your server environment, enable the <strong>Maps JavaScript API</strong>{' '}
        in Google Cloud, and restrict the key by HTTP referrer. Then reload the app.
      </Alert>
    );
  }

  return (
    <GoogleCompetitorMap
      apiKey={apiKey}
      propertyHotel={propertyHotel}
      propertyRate={propertyRate}
      propertyCurrency={propertyCurrency}
      competitors={competitors}
      accentColor={accentColor}
      isDark={isDark}
      focusCompetitorId={focusCompetitorId}
      onFocusApplied={onFocusApplied}
    />
  );
}

export default React.memo(CompetitorLocationMap);
